import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import { Button, Col, Form, Input, Row, Space, Upload, Avatar } from 'antd';
import { useDispatch, useSelector } from 'react-redux';
import { getUserById, updateUser } from '../../store/slices/userSlice';
import { Alert, Card, Descriptions, Select } from 'antd';
import { CheckCircleOutlined, CloseCircleOutlined, UserOutlined, UploadOutlined } from '@ant-design/icons';
import { useForm } from 'antd/es/form/Form';
import { getCodes } from '../../store/slices/codeSlice';

const EditCustomer = () => {
    const { id } = useParams();
    const [form] = useForm();
    const navigate = useNavigate();
    const [loading, setLoading] = useState(false);
    const dispatch = useDispatch();
    const [alertMessage, setAlertMessage] = useState("");
    const [alertType, setAlertType] = useState("success");
    const [type, setType] = useState("user");
    const { userData } = useSelector((state) => state?.user)
    const { codes } = useSelector((state) => state?.code)
    const [fileList, setFileList] = useState([]);

    useEffect(() => {
        dispatch(getUserById(id))
        dispatch(getCodes())
    }, [dispatch, id])

    const user = userData?.data?.user

    useEffect(() => {
        if (userData?.data?.user) {
            let isVerified = null
            if (userData?.data?.user?.is_verified == 0) {
                isVerified = "Block"
            } else if (userData?.data?.user?.is_verified == 1) {
                isVerified = "UnBlock"
            }
            const defaultImage = userData?.data?.user?.profile_image
                ? [
                    {
                        uid: '-1',
                        name: userData.data.user.profile_image.split('/').pop(),
                        status: 'done',
                        url: userData.data.user.profile_image
                    }
                ]
                : [];
            setFileList(defaultImage);
            form.setFieldsValue({
                is_verified : isVerified,
                first_name: user?.first_name,
                last_name: user?.last_name,
                email: user?.email,
                phone: user?.phone,
                gender: user?.gender,
                code: user?.code_id,
            })
        }
    }, [form, userData])

    const onSubmit = async (values) => {
        setLoading(true);
        setAlertMessage("");
        try {
            await new Promise(resolve => setTimeout(resolve, 1000));
            let isVerified = null
            if (values?.is_verified == "Block") {
                isVerified = 0
            } else if (values?.is_verified == "UnBlock") {
                isVerified = 1
            } else {
                isVerified = userData?.data?.user?.is_verified
            }

            const formData = new FormData();

            formData.append('id', id);
            if (isVerified != null) {
                formData.append('is_verified', isVerified);
            }
            if (values?.code) {
                formData.append('code_id', values?.code);
            }
            formData.append('first_name', values?.first_name);
            formData.append('last_name', values?.last_name);
            formData.append('phone', values?.phone);
            formData.append('gender', values?.gender);
            if (values?.image && values?.image?.file) {
                formData.append('image', values?.image?.file);
            }

            const resultAction = await dispatch(updateUser(formData))
            setType('agent')

            if (updateUser.fulfilled.match(resultAction)) {
                setAlertMessage(resultAction?.payload?.message || 'Agent updated successfully!');
                setAlertType('success');
                setTimeout(() => {
                    setAlertMessage('');
                }, 4000);
            } else {
                console.log(resultAction?.payload)
                setAlertMessage(resultAction?.payload?.error || 'Error updating agent');
                setAlertType('error');
            }
        } catch (error) {
            setAlertMessage('Error updating agent.');
            setAlertType('error');
            console.log(error)
        } finally {
            setLoading(false);
        }
    };

    const handleUploadChange = ({ fileList }) => {
        setFileList(fileList.slice(-1)); // single image only
    };

    const beforeUpload = (file) => {
        const isImage = file.type.startsWith('image/');
        if (!isImage) {
            setAlertMessage('You can only upload image files!');
            setAlertType('error');
            return Upload.LIST_IGNORE;
        }
        return false; // prevent auto upload
    };

    return (
        <div>
            <h1 style={{ marginBottom: 24 }}>Customer</h1>
            <Card
                title={
                    <span style={{ fontSize: '18px', fontWeight: 600 }}>
                        <UserOutlined style={{ marginRight: 8, color: '#CA9AE8' }} />
                        Edit Customer
                    </span>
                }
                style={{
                    marginBottom: 24,
                    borderRadius: '12px',
                    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
                    border: 'none',
                }}
            >
                {alertMessage && type === "agent" && (
                    <Alert
                        message={alertMessage}
                        type={alertType}
                        showIcon
                        icon={
                            alertType === 'success'
                                ? <CheckCircleOutlined />
                                : <CloseCircleOutlined />
                        }
                        closable
                        onClose={() => setAlertMessage('')}
                        style={{ marginBottom: 24, borderRadius: '8px' }}
                    />
                )}

                <Form
                    layout="vertical"
                    form={form}
                    onFinish={onSubmit}
                    initialValues={{
                        first_name: user?.first_name,
                        last_name: user?.last_name,
                        email: user?.email,
                        phone: user?.phone,
                        gender: user?.gender,
                        is_verified: user?.is_verified ? 'UnBlock' : 'Block',
                        code: user?.code?._id,
                    }}
                >
                    <Row gutter={16}>
                        <Col xs={24} md={12}>
                            <Form.Item
                                label="First Name"
                                name="first_name"
                                rules={[{ required: true, message: 'First name is required' }]}
                            >
                                <Input placeholder="Enter first name" />
                            </Form.Item>
                        </Col>

                        <Col xs={24} md={12}>
                            <Form.Item
                                label="Last Name"
                                name="last_name"
                                rules={[{ required: true, message: 'Last name is required' }]}
                            >
                                <Input placeholder="Enter last name" />
                            </Form.Item>
                        </Col>

                        <Col xs={24} md={12}>
                            <Form.Item
                                label="Email"
                                name="email"
                                rules={[
                                    { required: true, message: 'Email is required' },
                                    { type: 'email', message: 'Enter a valid email' },
                                ]}
                            >
                                <Input placeholder="Enter email" />
                            </Form.Item>
                        </Col>

                        <Col xs={24} md={12}>
                            <Form.Item
                                label="Phone"
                                name="phone"
                                rules={[{ required: true, message: 'Phone number is required' }]}
                            >
                                <Input placeholder="Enter phone number" />
                            </Form.Item>
                        </Col>

                        <Col xs={24} md={12}>
                            <Form.Item label="Gender" name="gender">
                                <Select
                                    placeholder="Select gender"
                                    options={[
                                        { value: 'male', label: 'Male' },
                                        { value: 'female', label: 'Female' },
                                        { value: 'other', label: 'Other' },
                                    ]}
                                />
                            </Form.Item>
                        </Col>

                        <Col xs={24} md={12}>
                            <Form.Item label="Block / Unblock" name="is_verified">
                                <Select
                                    options={[
                                        { value: 'Block', label: 'Block' },
                                        { value: 'UnBlock', label: 'UnBlock' },
                                    ]}
                                />
                            </Form.Item>
                        </Col>

                        <Col xs={24}>
                            <Form.Item label="Code" name="code">
                                <Select
                                    placeholder="Select code"
                                    options={codes?.data?.map((item) => ({
                                        value: item?._id,
                                        label: item?.code,
                                    }))}
                                />
                            </Form.Item>
                        </Col>
                    </Row>
                    <Form.Item name="image" label="Profile Image">
                        <Upload
                            listType="picture-card"
                            fileList={fileList}
                            beforeUpload={beforeUpload}
                            onChange={handleUploadChange}
                            // beforeUpload={() => false} // prevent auto upload
                            maxCount={1}
                            accept="image/*"
                        >
                            {fileList.length >= 1 ? null : (
                                <div>
                                    <UploadOutlined />
                                    <div style={{ marginTop: 8 }}>Upload</div>
                                </div>
                            )}
                        </Upload>
                    </Form.Item>


                    <Form.Item>
                        <Space>
                            <Button
                                type="primary"
                                htmlType="submit"
                                loading={loading}
                                size="large"
                                style={{
                                    height: '48px',
                                    fontSize: '16px',
                                    fontWeight: 600,
                                    borderRadius: '8px',
                                }}
                            >
                                Save Changes
                            </Button>
                            <Button size="large" onClick={() => navigate(-1)}>
                                Back
                            </Button>
                        </Space>
                    </Form.Item>
                </Form>
            </Card>

        </div>
    );
};

export default EditCustomer;